/**
 * AgentForge shell — Forge Suite chrome + operator studio.
 * Brain: HF models · Cloudflare · CivitAI · Forge endpoints.
 */

import { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  PiCircleNotchBold,
  PiGoogleLogoBold,
  PiSignOutBold,
  PiStopBold,
  PiWalletBold,
  PiShieldWarningBold,
  PiPlayBold,
  PiDownloadSimpleBold,
  PiBookmarksBold,
} from "react-icons/pi";

import { BackgroundFx } from "./BackgroundFx";
import { BrainPicker } from "./BrainPicker";
import {
  brainToInferenceTarget,
  consumeBinderQuery,
  loadEndpoint,
  probeEndpointHealth,
  type AgentBrain,
} from "../lib/endpoint";
import { isBilledSource } from "../suite/inference";
import {
  AGENTFORGE_HF_BUCKET,
  AGENTFORGE_HF_BUCKET_URI,
} from "../lib/hfBucket";
import {
  applyTokensFromHash,
  clearSession,
  fetchMe,
  isLoggedIn,
  startGoogleLoginWeb,
} from "../suite/auth";
import { SUITE_MEMBER_PRICE_USD } from "../suite/access";
import type { SuiteUser, WalletSnapshot } from "../suite/types";
import {
  fetchWallet,
  fetchWalletHistory,
  startCheckout,
  subscribeMember,
  type WalletHistoryPayload,
} from "../suite/wallet";
import { getStoredThemeId, selectTheme } from "../themes/applyTheme";
import { BG_FX_OPTIONS, getBgFxId, setBgFxId } from "../themes/bgFx";
import { listThemes } from "../themes/registry";
import { listPickerMotions, listPickerThemes, SUITE_SKINS_EVENT } from "../themes/suiteBridge";
import type { ThemeId } from "../themes/types";
import { runAgentLoop } from "../agent/agentLoop";
import type { AgentAction, AgentRun, AgentStep, AgentPreset } from "../agent/types";
import { SUITE_STEP_COST_HINT_USD } from "../agent/types";
import { SiteQaPanel } from "../qa/SiteQaPanel";
import {
  addPreset,
  downloadRun,
  getSaveArtifacts,
  getTrustConsent,
  loadPresets,
  loadRuns,
  setSaveArtifacts,
  setTrustConsent,
  upsertRun,
} from "../agent/runStore";

import "../styles/forge-suite.css";
import "../styles/forge-chrome.css";

const SUITE_HOME = "https://forgesuiteui.com";

function newRunId(): string {
  return `run_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function stepId(): string {
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

export const ForgeChrome = memo(() => {
  const [endpoint, setEndpoint] = useState<AgentBrain | null>(() => loadEndpoint());
  const [user, setUser] = useState<SuiteUser | null>(null);
  const [wallet, setWallet] = useState<WalletSnapshot | null>(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [health, setHealth] = useState<string | null>(null);
  const [showSkins, setShowSkins] = useState(false);
  const [themeId, setThemeId] = useState<ThemeId>(() => getStoredThemeId());
  const [fxId, setFxId] = useState(() => getBgFxId());
  const [pickerSkins, setPickerSkins] = useState(() => listPickerThemes(listThemes()));
  const [pickerMotions, setPickerMotions] = useState(() =>
    listPickerMotions(BG_FX_OPTIONS),
  );
  useEffect(() => {
    const sync = () => {
      setPickerSkins(listPickerThemes(listThemes()));
      setPickerMotions(listPickerMotions(BG_FX_OPTIONS));
    };
    window.addEventListener(SUITE_SKINS_EVENT, sync);
    const t = window.setTimeout(sync, 400);
    return () => {
      window.removeEventListener(SUITE_SKINS_EVENT, sync);
      window.clearTimeout(t);
    };
  }, []);
  const [showAbout, setShowAbout] = useState(false);
  const [walletHist, setWalletHist] = useState<WalletHistoryPayload | null>(null);
  const [walletHistErr, setWalletHistErr] = useState<string | null>(null);
  /** usable-ui: rails collapse so Operator (goal + steps) owns FOV */
  const [brainCollapsed, setBrainCollapsed] = useState(() => {
    try {
      return localStorage.getItem("agentforge.brainRail.v1") !== "0";
    } catch {
      return true;
    }
  });
  const [historyCollapsed, setHistoryCollapsed] = useState(() => {
    try {
      return localStorage.getItem("agentforge.historyRail.v1") !== "0";
    } catch {
      return true;
    }
  });
  const persistBrainRail = (collapsed: boolean) => {
    setBrainCollapsed(collapsed);
    try {
      localStorage.setItem("agentforge.brainRail.v1", collapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  };
  const persistHistoryRail = (collapsed: boolean) => {
    setHistoryCollapsed(collapsed);
    try {
      localStorage.setItem("agentforge.historyRail.v1", collapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  };

  const [goal, setGoal] = useState("Open Notepad and type hello from AgentForge");
  const [consent, setConsent] = useState(() => getTrustConsent());
  const [saveArtifacts, setSaveArtifactsState] = useState(() => getSaveArtifacts());
  const [armed, setArmed] = useState(false);
  const [running, setRunning] = useState(false);
  const [statusLine, setStatusLine] = useState(
    "Connect a catalog brain, grant consent, then arm the agent",
  );
  const [steps, setSteps] = useState<AgentStep[]>([]);
  const [runs, setRuns] = useState<AgentRun[]>(() => loadRuns());
  const [presets, setPresets] = useState<AgentPreset[]>(() => loadPresets());
  const [costHint, setCostHint] = useState(0);
  const [showTrustModal, setShowTrustModal] = useState(() => !getTrustConsent());
  const [studioMode, setStudioMode] = useState<"operator" | "qa">(() => {
    try {
      return localStorage.getItem("agentforge.studioMode.v1") === "qa" ? "qa" : "operator";
    } catch {
      return "operator";
    }
  });
  const persistStudioMode = (mode: "operator" | "qa") => {
    setStudioMode(mode);
    try {
      localStorage.setItem("agentforge.studioMode.v1", mode);
    } catch {
      /* ignore */
    }
  };

  const stopRef = useRef(false);
  const activeRunRef = useRef<AgentRun | null>(null);

  const refreshAuth = useCallback(async () => {
    applyTokensFromHash();
    if (!isLoggedIn()) {
      setUser(null);
      setWallet(null);
      return;
    }
    try {
      const me = await fetchMe();
      setUser(me);
      const w = await fetchWallet();
      setWallet(w);
      try {
        setWalletHist(await fetchWalletHistory({ limit: 20 }));
        setWalletHistErr(null);
      } catch (e) {
        setWalletHistErr(e instanceof Error ? e.message : String(e));
      }
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    consumeBinderQuery();
    setEndpoint(loadEndpoint());
    void refreshAuth();
  }, [refreshAuth]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && (running || armed)) {
        e.preventDefault();
        stopRef.current = true;
        setRunning(false);
        setArmed(false);
        setStatusLine("Kill switch — agent stopped (Esc)");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [running, armed]);

  const checkHealth = async () => {
    if (!endpoint?.endpointUrl) {
      setHealth("No brain connected — pick HF, Cloudflare, CivitAI, or a Forge endpoint");
      return;
    }
    setHealth("Probing…");
    const r = await probeEndpointHealth(endpoint);
    setHealth(r.ok ? `OK · ${r.message}` : `Fail · ${r.message}`);
  };

  const acceptConsent = () => {
    setTrustConsent(true);
    setConsent(true);
    setShowTrustModal(false);
  };

  const stopAgent = () => {
    stopRef.current = true;
    setRunning(false);
    setArmed(false);
    setStatusLine("Stopped");
    if (activeRunRef.current) {
      const run: AgentRun = {
        ...activeRunRef.current,
        status: "stopped",
        endedAt: new Date().toISOString(),
        steps,
        estimatedCostUsd: costHint,
      };
      activeRunRef.current = null;
      setRuns(upsertRun(run));
    }
  };

  const startAgent = async () => {
    if (!consent) {
      setShowTrustModal(true);
      return;
    }
    if (!goal.trim()) {
      setStatusLine("Enter a goal first");
      return;
    }

    stopRef.current = false;
    setArmed(true);
    setRunning(true);
    setSteps([]);
    setCostHint(0);

    const target = brainToInferenceTarget(endpoint);
    const billSteps = Boolean(target && isBilledSource(target.source));

    const run: AgentRun = {
      id: newRunId(),
      goal: goal.trim(),
      status: "running",
      startedAt: new Date().toISOString(),
      steps: [],
      brainMode: endpoint?.source || "custom",
      brainUrl: endpoint?.endpointUrl,
    };
    activeRunRef.current = run;
    setStatusLine("Agent armed — planning…");

    let stepIndex = 0;
    const collected: AgentStep[] = [];

    const result = await runAgentLoop({
      goal: goal.trim(),
      target,
      billSteps,
      callbacks: {
        shouldStop: () => stopRef.current,
        onStatus: (msg) => setStatusLine(msg),
        onStep: ({ thought, action, status, error }) => {
          if (status === "running") {
            const s: AgentStep = {
              id: stepId(),
              index: stepIndex,
              at: new Date().toISOString(),
              thought,
              action: action as AgentAction,
              status: "running",
            };
            collected.push(s);
            stepIndex += 1;
            setSteps([...collected]);
          } else {
            const last = collected[collected.length - 1];
            if (last) {
              last.status = status === "ok" ? "ok" : "error";
              last.error = error;
              setSteps([...collected]);
            }
            if (billSteps) {
              setCostHint((c) => c + SUITE_STEP_COST_HINT_USD);
            }
          }
        },
      },
    });

    setRunning(false);
    setArmed(false);
    const final: AgentRun = {
      ...run,
      status:
        result.status === "done" ? "done" : result.status === "stopped" ? "stopped" : "failed",
      endedAt: new Date().toISOString(),
      steps: collected,
      estimatedCostUsd: result.estimatedCostUsd,
      notes: [
        "OS control runs in AgentForge Desktop companion when installed.",
        endpoint?.label ? `Brain: ${endpoint.label}` : "No remote brain",
      ],
    };
    activeRunRef.current = null;
    setRuns(upsertRun(final));
    setCostHint(result.estimatedCostUsd);
    setStatusLine(
      result.status === "done"
        ? `Done · ${result.steps} steps` +
          (result.estimatedCostUsd > 0 ? ` · ~$${result.estimatedCostUsd.toFixed(2)} hint` : "")
        : result.status === "stopped"
          ? "Stopped"
          : "Failed",
    );
  };

  return (
    <div className="af-shell-fill">
      <BackgroundFx />

      {(armed || running || !consent) && (
        <div className={`af-trust-banner ${running ? "af-trust-banner--live" : ""}`} role="status">
          <PiShieldWarningBold />
          <span>
            {running
              ? "AGENT ARMED — planning/control path active. Esc / Stop kills the loop."
              : !consent
                ? "This app can control mouse, keyboard, and see your screen when the desktop agent is armed. Consent required."
                : "Ready. Catalog brains plan steps; desktop companion executes OS actions."}
          </span>
          {(running || armed) && (
            <button type="button" className="af-kill" onClick={stopAgent} title="Stop (Esc)">
              <PiStopBold /> Stop
            </button>
          )}
        </div>
      )}

      <header className="af-chrome">
        <div className="af-chrome-brand">
          <img
            className="af-mark"
            src="/agentforge.png"
            width={32}
            height={32}
            alt=""
          />
          <div className="af-brand-text">
            <strong>AgentForge</strong>
            <span>Desktop automation · Forge Suite</span>
          </div>
        </div>

        <div className="af-chrome-actions">
          {endpoint && (
            <span className="af-brain-chip" title={endpoint.endpointUrl}>
              {endpoint.label || endpoint.source}
            </span>
          )}
          {isBilledSource(endpoint?.source) && (
            <span className="af-cost-chip">
              ~${SUITE_STEP_COST_HINT_USD.toFixed(2)}/step
              {costHint > 0 ? ` · ~$${costHint.toFixed(2)}` : ""}
            </span>
          )}
          <button type="button" className="af-chip" onClick={() => void checkHealth()}>
            Health
          </button>
          {health && <span className="af-health">{health}</span>}
          <button type="button" className="af-chip" onClick={() => setShowSkins((v) => !v)}>
            Skins
          </button>
          {wallet != null && (
            <span className="af-wallet-chip" title="Suite wallet">
              <PiWalletBold /> ${wallet.balanceUsd.toFixed(2)}
            </span>
          )}
          {user ? (
            <button
              type="button"
              className="af-chip"
              onClick={() => {
                clearSession();
                setUser(null);
                setWallet(null);
              }}
            >
              <PiSignOutBold /> {user.email?.split("@")[0] || "Out"}
            </button>
          ) : (
            <button
              type="button"
              className="af-chip af-chip--accent"
              disabled={authBusy}
              onClick={() => {
                setAuthBusy(true);
                startGoogleLoginWeb();
              }}
            >
              <PiGoogleLogoBold /> Google
            </button>
          )}
          <a className="af-chip" href={SUITE_HOME} target="_blank" rel="noreferrer">
            Suite
          </a>
          <button type="button" className="af-chip" onClick={() => setShowAbout(true)}>
            About
          </button>
        </div>
      </header>

      {showSkins && (
        <div className="af-skins-panel">
          <div className="af-skins-col">
            <h4>Skins</h4>
            <div className="af-skin-grid">
              {pickerSkins.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`af-skin-card ${themeId === t.id ? "af-skin-card--on" : ""}`}
                  onClick={() => {
                    selectTheme(t.id);
                    setThemeId(t.id);
                  }}
                >
                  <span
                    className="af-skin-swatches"
                    style={{
                      background: `linear-gradient(135deg, ${t.preview[0]}, ${t.preview[1]}, ${t.preview[2]})`,
                    }}
                  />
                  <span>{t.name}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="af-skins-col">
            <h4>Motion</h4>
            <div className="af-fx-grid">
              {pickerMotions.map((fx) => (
                <button
                  key={fx.id}
                  type="button"
                  className={`af-fx-chip ${fxId === fx.id ? "af-fx-chip--on" : ""}`}
                  onClick={() => {
                    setBgFxId(fx.id);
                    setFxId(fx.id);
                  }}
                >
                  {fx.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <main className="af-main af-main--3">
        {brainCollapsed ? (
          <button
            type="button"
            className="af-rail-collapsed"
            title="Show brain picker"
            onClick={() => persistBrainRail(false)}
          >
            <span>»</span>
            <span className="af-rail-label">Brain</span>
          </button>
        ) : (
          <div className="af-panel--brain-wrap">
            <div className="af-panel-head af-rail-toolbar">
              <span className="af-muted" style={{ fontSize: 11, fontWeight: 700 }}>
                Brain
              </span>
              <button
                type="button"
                className="af-chip"
                title="Collapse brain rail"
                onClick={() => persistBrainRail(true)}
              >
                «
              </button>
            </div>
            <BrainPicker
              brain={endpoint}
              onBrainChange={setEndpoint}
              wallet={wallet}
              onStatus={setStatusLine}
            />
          </div>
        )}

        <section className="af-panel af-panel--operator">
          <div className="af-panel-head">
            <h2>{studioMode === "qa" ? "Site QA" : "Operator"}</h2>
            <div className="af-mode-strip" role="tablist" aria-label="Studio mode">
              <button
                type="button"
                role="tab"
                aria-selected={studioMode === "operator"}
                className={`af-mode-btn ${studioMode === "operator" ? "af-mode-btn--on" : ""}`}
                onClick={() => persistStudioMode("operator")}
              >
                Goal
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={studioMode === "qa"}
                className={`af-mode-btn ${studioMode === "qa" ? "af-mode-btn--on" : ""}`}
                onClick={() => persistStudioMode("qa")}
              >
                Site QA
              </button>
            </div>
          </div>

          {studioMode === "qa" ? (
            <SiteQaPanel
              brainTarget={brainToInferenceTarget(endpoint)}
              onStatus={setStatusLine}
            />
          ) : (
            <>
          <p className="af-muted" style={{ marginTop: 0, flexShrink: 0 }}>
            Goal + live steps. Expand Brain / History rails as needed.
          </p>

          <label className="af-goal-label">
            Goal
            <textarea
              className="af-goal"
              rows={3}
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="Tell it what to do…"
              disabled={running}
            />
          </label>

          <div className="af-run-controls">
            {!running ? (
              <button type="button" className="af-btn af-btn--primary" onClick={() => void startAgent()}>
                <PiPlayBold /> Arm &amp; run
              </button>
            ) : (
              <button type="button" className="af-btn af-btn--danger" onClick={stopAgent}>
                <PiStopBold /> Stop
              </button>
            )}
            <button
              type="button"
              className="af-btn"
              onClick={() => {
                if (!goal.trim()) return;
                setPresets(addPreset(goal.trim().slice(0, 40), goal.trim()));
              }}
            >
              <PiBookmarksBold /> Save preset
            </button>
            <label className="af-check">
              <input
                type="checkbox"
                checked={saveArtifacts}
                onChange={(e) => {
                  setSaveArtifacts(e.target.checked);
                  setSaveArtifactsState(e.target.checked);
                }}
              />
              Save run artifacts (screenshots default off)
            </label>
          </div>

          <div className="af-status-line">
            {running && <PiCircleNotchBold className="af-spin" />}
            <span>{statusLine}</span>
          </div>

          <h3>Step trace</h3>
          <ol className="af-step-list">
            {steps.length === 0 && <li className="af-muted">No steps yet.</li>}
            {steps.map((s) => (
              <li key={s.id} className={`af-step af-step--${s.status}`}>
                <span className="af-step-type">{s.action.type}</span>
                <span className="af-step-sum">{s.action.summary}</span>
                {s.thought && <span className="af-step-thought">{s.thought}</span>}
              </li>
            ))}
          </ol>
            </>
          )}
        </section>

        {historyCollapsed ? (
          <button
            type="button"
            className="af-rail-collapsed"
            title="Show history"
            onClick={() => persistHistoryRail(false)}
          >
            <span>«</span>
            <span className="af-rail-label">History</span>
          </button>
        ) : (
        <aside className="af-panel af-panel--side">
          <div className="af-panel-head">
            <h2 style={{ margin: 0 }}>Presets</h2>
            <button
              type="button"
              className="af-chip"
              title="Collapse history"
              onClick={() => persistHistoryRail(true)}
            >
              »
            </button>
          </div>
          <ul className="af-list">
            {presets.length === 0 && <li className="af-muted">Save a playbook from your goal.</li>}
            {presets.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className="af-list-btn"
                  onClick={() => setGoal(p.goal)}
                  disabled={running}
                >
                  {p.name}
                </button>
              </li>
            ))}
          </ul>

          <h2>Run history</h2>
          <ul className="af-list">
            {runs.length === 0 && <li className="af-muted">Export runs as JSON under ./data/runs.</li>}
            {runs.slice(0, 12).map((r) => (
              <li key={r.id} className="af-run-row">
                <div>
                  <strong>{r.status}</strong>
                  <span className="af-muted"> · {new Date(r.startedAt).toLocaleString()}</span>
                  <div className="af-run-goal">{r.goal}</div>
                </div>
                <button type="button" className="af-chip" onClick={() => downloadRun(r)}>
                  <PiDownloadSimpleBold />
                </button>
              </li>
            ))}
          </ul>

          <h2>Wallet</h2>
          {user ? (
            <div className="af-wallet-box">
              <p>
                Balance: <strong>${(wallet?.balanceUsd ?? 0).toFixed(2)}</strong>
                {wallet?.subscriptionActive ? " · Member" : ""}
              </p>
              <div className="af-pack-row">
                {(wallet?.packs || [
                  { amountUsd: 5, label: "$5" },
                  { amountUsd: 10, label: "$10" },
                  { amountUsd: 20, label: "$20" },
                ]).map((p) => (
                  <button
                    key={p.amountUsd}
                    type="button"
                    className="af-chip"
                    onClick={async () => {
                      try {
                        const url = await startCheckout(p.amountUsd);
                        if (url) window.location.href = url;
                      } catch (e) {
                        setStatusLine(e instanceof Error ? e.message : String(e));
                      }
                    }}
                  >
                    Add {p.label}
                  </button>
                ))}
                <button
                  type="button"
                  className="af-chip af-chip--accent"
                  onClick={async () => {
                    try {
                      const url = await subscribeMember();
                      if (url) window.location.href = url;
                    } catch (e) {
                      setStatusLine(e instanceof Error ? e.message : String(e));
                    }
                  }}
                >
                  Member ${SUITE_MEMBER_PRICE_USD}/mo
                </button>
              </div>
              <h3 className="af-muted" style={{ marginTop: 12, fontSize: 12 }}>
                History
              </h3>
              {walletHistErr ? (
                <p className="af-muted">{walletHistErr}</p>
              ) : null}
              {walletHist && !walletHist.items.length ? (
                <p className="af-muted">
                  No events yet. History starts when this tab shipped.
                </p>
              ) : null}
              <ul className="af-list" style={{ maxHeight: 180, overflow: "auto" }}>
                {(walletHist?.items || []).map((row) => (
                  <li key={row.id} className="af-run-row">
                    <div>
                      <strong>
                        {row.kind === "debit" ? "−" : "+"}${(row.amount_cents / 100).toFixed(2)}
                      </strong>
                      <span className="af-muted">
                        {" "}
                        · {row.suite} · {row.product}
                      </span>
                      <div className="af-run-goal">{row.reason}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="af-muted">Sign in with Google for the shared suite wallet and inference.</p>
          )}

          <h2>Data</h2>
          <p className="af-muted af-mono">
            {AGENTFORGE_HF_BUCKET}
            <br />
            {AGENTFORGE_HF_BUCKET_URI}
          </p>
        </aside>
        )}
      </main>

      {showTrustModal && (
        <div className="af-modal-backdrop" role="dialog" aria-modal>
          <div className="af-modal">
            <h2>
              <PiShieldWarningBold /> Computer control consent
            </h2>
            <p>
              <strong>AgentForge</strong> is a desktop GUI agent under Forge Suite. When armed
              (especially with the desktop companion), it can see your screen and control mouse,
              keyboard, and scroll.
            </p>
            <ul>
              <li>Kill switch always available (Esc / Stop)</li>
              <li>You are responsible for automation you run</li>
              <li>Screenshots are not uploaded unless you enable save artifacts</li>
              <li>Catalog brains bill via the shared suite wallet</li>
            </ul>
            <p className="af-muted">
              VisionForge Live is webcam gestures. AgentForge is full Operator-style desktop automation.
            </p>
            <div className="af-modal-actions">
              <button type="button" className="af-btn af-btn--primary" onClick={acceptConsent}>
                I understand — enable agent features
              </button>
              <button type="button" className="af-btn" onClick={() => setShowTrustModal(false)}>
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      {showAbout && (
        <div
          className="af-modal-backdrop"
          role="dialog"
          aria-modal
          onClick={() => setShowAbout(false)}
        >
          <div className="af-modal" onClick={(e) => e.stopPropagation()}>
            <h2>About AgentForge</h2>
            <p>
              Forge Suite desktop automation: natural language goals, catalog brains
              (HF, Cloudflare, CivitAI, Forge endpoints), and optional desktop companion
              for OS control. Product brand is AgentForge / Forge Suite.
            </p>
            <p>
              Accent <code>#a3e635</code> · Debit client <code>agentforge</code> · HF{" "}
              <code>{AGENTFORGE_HF_BUCKET}</code>
            </p>
            <p className="af-muted">
              Desktop engine includes open-source components under Apache-2.0 (see NOTICE). We do not
              claim ownership of third-party engines or model weights.
            </p>
            <button type="button" className="af-btn" onClick={() => setShowAbout(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

ForgeChrome.displayName = "ForgeChrome";
